/**
 * The categories of a business (docs/08-entscheidungen.md, S12): a list in a fixed order, kept like
 * the allergens. A dish names one of them or none; the server turns away any other (as a foreign
 * key). The name is the key: renaming a category renames it at every dish. Levels are split by
 * „ / “ as before („Warm / Meeresfrüchte“); the order of the list is the order of the filters and of
 * „Abschnitte aus Kategorien“.
 */
export const MAX_CATEGORIES = 100;

/** The categories of the drinks every business begins with (S22), the parts of a usual drinks menu. */
export const DEFAULT_DRINK_CATEGORIES: readonly string[] = ["Aperitif", "Alkoholfreie Getränke", "Biere", "Offene Weine", "Flaschenweine", "Heißgetränke", "Spirituosen"];
export const MAX_CATEGORY_LENGTH = 80;

const oneLine = (s: unknown) => (typeof s === "string" ? s.normalize("NFC").replace(/\s+/g, " ").trim() : "");

/**
 * The levels of a category split at a „/“ with a space beside it: „Warm / Meeresfrüchte“ has two,
 * „Kalt/Warm / Veggie“ too („Kalt/Warm“ is one name).
 */
const LEVEL = /\s+\/\s*|\s*\/\s+/;

/** „Warm / Meeresfrüchte“ with even spaces around each level's „/“. */
export const cleanCategory = (raw: unknown) =>
  oneLine(raw)
    .split(LEVEL)
    .map((p) => p.trim())
    .filter(Boolean)
    .join(" / ")
    .slice(0, MAX_CATEGORY_LENGTH);

/** The first level of a category: „Warm / Meeresfrüchte“ → „Warm“, „Kalt/Warm / Veggie“ → „Kalt/Warm“. */
export const topCategory = (category: string) => category.split(LEVEL)[0]!.trim();

/** The second level of a category (S44): „Carne / Kalb“ → „Kalb“; „Carne“ → „“. Deeper levels stay with it. */
export const secondCategory = (category: string) => category.split(LEVEL).slice(1).join(" / ").trim();

/** A category as entered in the list; `from`: the name it had, when it was renamed. */
export interface CategoryEntry {
  name: string;
  from?: string;
}

const key = (name: string) => name.toLocaleLowerCase("de-DE");

/** The list as entered: names cleaned, each once (without regard to case), none empty. */
export function cleanCategories(input: unknown): { value: CategoryEntry[] } | { error: string } {
  if (!Array.isArray(input)) return { error: "Ungültige Liste" };
  if (input.length > MAX_CATEGORIES) return { error: `Höchstens ${MAX_CATEGORIES} Kategorien` };
  const out: CategoryEntry[] = [];
  for (const raw of input) {
    const o = (raw ?? {}) as Record<string, unknown>;
    const name = cleanCategory(o.name);
    if (name === "") return { error: "Bitte jeder Kategorie einen Namen geben." };
    if (out.some((c) => key(c.name) === key(name))) return { error: `Die Kategorie „${name}“ steht zweimal in der Liste.` };
    const from = typeof o.from === "string" && o.from.trim() !== "" ? cleanCategory(o.from) : undefined;
    out.push(from && from !== name ? { name, from } : { name });
  }
  return { value: out };
}

/**
 * The category of the list a dish means: the same name, without regard to case and spaces around
 * „/“; empty is none. Anything else is an error, as for an unknown allergen code.
 */
export function resolveCategory(raw: unknown, list: readonly string[]): { value: string } | { error: string } {
  const name = cleanCategory(raw);
  if (name === "") return { value: "" };
  const found = list.find((c) => key(c) === key(name));
  return found ? { value: found } : { error: `Unbekannte Kategorie „${name}“` };
}

/** The categories the dishes name, in the order of their numbers (for a business before S12). */
export function categoriesOfDishes(dishes: Iterable<{ category: string; number: number }>): string[] {
  const out: string[] = [];
  for (const d of [...dishes].sort((a, b) => a.number - b.number)) if (d.category && !out.some((c) => key(c) === key(d.category))) out.push(d.category);
  return out;
}

/** The first levels of the list, in its order: „Kalt“, „Warm“, „Süß“. */
export function topLevels(list: readonly string[]): string[] {
  const out: string[] = [];
  for (const c of list) {
    const top = topCategory(c);
    if (top && !out.includes(top)) out.push(top);
  }
  return out;
}

/** The place of a category in the list, for sorting; unknown ones last. */
export const categoryRank = (list: readonly string[]) => {
  const rank = new Map(list.map((c, i) => [key(c), i]));
  const tops = topLevels(list);
  return {
    of: (category: string) => rank.get(key(category)) ?? list.length,
    ofTop: (top: string) => (tops.includes(top) ? tops.indexOf(top) : tops.length),
  };
};
